import React, { useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { AudioPlayerProps } from '../type';

/**
 *
 * @param seconds - seconds
 * @returns - time string in the format of "hh:mm:ss", ex: "01:02:03", "02:03", "00:03"
 */
const getTimeString = (seconds: number) => {
  const remainingHours = Math.floor(seconds / 3600);
  const remainingMinutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (remainingHours > 0) {
    return `${remainingHours.toString().padStart(2, '0')}:${remainingMinutes
      .toString()
      .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  if (remainingMinutes > 0) {
    return `${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  }
  return `00:${remainingSeconds.toString().padStart(2, '0')}`;
};

const PlaybackRateOptions = [
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '1.25x', value: 1.25 },
  { label: '1.5x', value: 1.5 },
];

type Props = AudioPlayerProps & {
  onDownload?: () => void;
  onPlayPause: () => void;
  onSeek: (value: number) => void;
  onSeekBackward: () => void;
  onSeekForward: () => void;
  duration: number; // seconds
  title: string;
  time: number; // seconds
  isPlaying: boolean;
  // Optional playback rate support (web provides handler)
  onChangeRate?: (rate: number) => void;
  playbackRate?: number;
};
export const Player: React.FC<Props> = (props) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const {
    onDownload,
    onPlayPause,
    duration,
    onSeekBackward,
    onSeekForward,
    title,
    time,
    isPlaying,
    onChangeRate,
    playbackRate = 1,
  } = props;
  const progress = duration > 0 ? Math.min(Math.max(time / duration, 0), 1) : 0;
  const backgroundColor =
    props.style?.backgroundColor || props.attributes?.backgroundColor || '#fff';
  const sliderColor = props.style?.color || props.attributes?.color || '#000';
  const containerStyle = {
    height: props.style?.height || props?.height,
  };
  return (
    <View
      style={{
        padding: 20,
        alignItems: 'center',
        position: 'relative',
        backgroundColor,
        ...containerStyle,
      }}
    >
      <Text
        style={{
          fontSize: 16,
          marginBottom: 10,
          minHeight: 20,
        }}
        numberOfLines={1}
      >
        {title}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Text
          style={{
            fontSize: 11,
            marginHorizontal: 4,
          }}
        >
          {getTimeString(time)}
        </Text>
        <View
          style={{
            flex: 1,
            height: 16,
            justifyContent: 'center',
            marginHorizontal: 10,
          }}
        >
          <View
            style={{
              height: 4,
              backgroundColor: '#E5E7EB',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                height: '100%',
                width: `${progress * 100}%`,
                backgroundColor: sliderColor,
              }}
            />
          </View>
        </View>
        <Text
          style={{
            fontSize: 11,
            marginHorizontal: 4,
          }}
        >
          {getTimeString(duration)}
        </Text>
        <TouchableOpacity
          style={{
            marginLeft: 8,
            padding: 8,
          }}
          onPress={() => setMenuVisible(!menuVisible)}
          accessibilityRole="button"
          accessibilityLabel="More options"
        >
          <Text
            style={{
              fontSize: 20,
            }}
          >
            ⋮
          </Text>
        </TouchableOpacity>
      </View>
      {menuVisible ? (
        <View
          style={{
            position: 'absolute',
            right: 20,
            top: 70,
            backgroundColor: '#ffffff',
            borderRadius: 6,
            shadowColor: '#000',
            shadowOpacity: 0.1,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 2 },
            elevation: 3,
            zIndex: 1000,
          }}
        >
          {onDownload ? (
            <TouchableOpacity
              style={{
                paddingVertical: 10,
                paddingHorizontal: 14,
                minWidth: 140,
              }}
              onPress={() => {
                // eslint-disable-next-line no-console
                setMenuVisible(false);
                onDownload();
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                }}
              >
                Download
              </Text>
            </TouchableOpacity>
          ) : null}

          {/* Divider */}
          {onDownload && onChangeRate ? (
            <View
              style={{
                height: 1,
                backgroundColor: '#eee',
                marginVertical: 4,
              }}
            />
          ) : null}

          {onChangeRate ? (
            <View
              style={{
                marginVertical: 4,
              }}
            >
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  opacity: 0.5,
                  marginHorizontal: 14,
                  marginVertical: 4,
                }}
              >
                Playback
              </Text>
              {PlaybackRateOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 14,
                    minWidth: 140,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  onPress={() => {
                    onChangeRate(option.value);
                    setMenuVisible(false);
                  }}
                >
                  <Text style={{ fontSize: 14 }}>{option.label}</Text>
                  {playbackRate === option.value ? <Text>✓</Text> : null}
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
        </View>
      ) : null}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-around',
          width: '80%',
          marginVertical: 10,
        }}
      >
        <TouchableOpacity onPress={onSeekBackward}>
          <Icon name={'replay-5'} size={28} color="#000" />
        </TouchableOpacity>
        <TouchableOpacity onPress={onPlayPause}>
          <Icon
            name={isPlaying ? 'pause-circle-outline' : 'play-circle-outline'}
            size={28}
            color="#000"
          />
        </TouchableOpacity>
        <TouchableOpacity onPress={onSeekForward}>
          <Icon name={'forward-5'} size={28} color="#000" />
        </TouchableOpacity>
      </View>
    </View>
  );
};
